import React, { useRef } from 'react'
import { Form } from 'react-bootstrap'
import { Updater } from 'widgets-for-react'
import { FormControls, FormNotification } from '../../../Shared/Frontend/forms'
import { getFSClassName } from '../../../Shared/Frontend/utils/getClassNameFromStyle'
import { slugify } from '../../../Shared/Frontend/utils/slugify'
import { Question } from './jotform.state'
import {
  checkBoxErrorMessage,
  checkboxExtraOptionErrorMessage,
  emailExtraConfirmationErrorMessage,
  emailInvalidErrorMessage,
  firstRequiredInvalid,
  fullNameRequiredErrorMessage,
  hasErrors,
  lastRequiredInvalid,
  phoneInvalidErrorMessage,
  radioExtraOptionErrorMessage,
  stringValueRequiredErrorMessage,
} from './jotform.utils'
import { t } from 'i18next'

export const JotformLayout = (props: {
  questions: (Question & { setQuestion: (_: Updater<Question>) => void })[]
  clickedSubmit: boolean
  submitting: boolean
  submit: () => void
}) => {
  const formHasErrors = props.questions.some(hasErrors)

  return (
    <form
      className="ls-container ls-gutter"
      aria-live="polite"
      onSubmit={(e) => {
        e.preventDefault()
        props.submit()
        if (formHasErrors) e.currentTarget.scrollIntoView({ behavior: 'smooth' })
      }}
    >
      {props.clickedSubmit && formHasErrors && (
        <FormNotification message="Er zijn fouten op het formulier. Los deze s.v.p. op voor je verder gaat." />
      )}

      {props.questions.map((formField) => (
        <FormField field={formField} submitting={props.submitting} />
      ))}
    </form>
  )
}

const FormField = (props: {
  field: Question & { setQuestion: (_: Updater<Question>) => void }
  submitting: boolean
}) => {
  const fullNameRef = useRef<HTMLFieldSetElement>(null)
  const { field, submitting } = props
  if (field.hidden) return <></>
  const setShowErrors = () =>
    field.setQuestion((state): Question => (state.kind == 'submitButton' ? state : { ...state, showErrors: true }))
  switch (field.kind) {
    case 'input': {
      return (
        <FormControls.FormItemInGroup
          {...field}
          controlId={field.id}
          disabled={submitting}
          onChange={(newValue) =>
            field.setQuestion((state): Question => (state.kind === 'input' ? { ...state, value: newValue } : state))
          }
          onBlur={setShowErrors}
          errorMessage={field.showErrors ? stringValueRequiredErrorMessage(field) : undefined}
        />
      )
    }
    case 'textarea': {
      return (
        <FormControls.FormItemInGroup
          {...field}
          controlId={field.id}
          as="textarea"
          disabled={submitting}
          rows={6}
          onChange={(newValue) =>
            field.setQuestion((state): Question => (state.kind === 'textarea' ? { ...state, value: newValue } : state))
          }
          onBlur={setShowErrors}
          errorMessage={field.showErrors ? stringValueRequiredErrorMessage(field) : undefined}
        />
      )
    }
    case 'number': {
      return (
        <FormControls.FormItemInGroup
          {...field}
          controlId={field.id}
          inputMode="numeric"
          type="number"
          disabled={submitting}
          min={field.minValue}
          max={field.maxValue}
          onChange={(newValue) =>
            field.setQuestion((state): Question => (state.kind === 'number' ? { ...state, value: newValue } : state))
          }
          onBlur={setShowErrors}
          errorMessage={field.showErrors ? stringValueRequiredErrorMessage(field) : undefined}
        />
      )
    }
    case 'select': {
      const options = field.options.map((option) => ({ label: option, value: option }))
      return (
        <FormControls.FormSelectInGroup
          {...field}
          controlId={field.id}
          disabled={submitting}
          options={
            field.placeholder
              ? [{ value: '', label: field.placeholder }, ...options]
              : options.map((option, index) => ({ ...option, disabled: field.placeholder && index === 0 }))
          }
          onChange={(newValue) =>
            field.setQuestion((state): Question => (state.kind === 'select' ? { ...state, value: newValue } : state))
          }
          onBlur={setShowErrors}
          errorMessage={field.showErrors ? stringValueRequiredErrorMessage(field) : undefined}
        />
      )
    }
    case 'email': {
      return (
        <div className={field.extraConfirmationField ? 'fs__row row--50-50' : 'fs__column'}>
          <FormControls.FormItemInGroup
            {...field}
            controlId={field.id}
            inputMode="email"
            disabled={submitting}
            onChange={(newValue) =>
              field.setQuestion((state): Question => (state.kind === 'email' ? { ...state, value: newValue } : state))
            }
            onBlur={setShowErrors}
            errorMessage={
              field.showErrors ? stringValueRequiredErrorMessage(field) || emailInvalidErrorMessage(field) : undefined
            }
          />
          {field.extraConfirmationField && (
            <FormControls.FormItemInGroup
              controlId={`${field.id}-confirm`}
              inputMode="email"
              label={t("Form:Label.confirmationEmail")}
              value={field.extraConfirmationField.value}
              placeholder={field.extraConfirmationField.placeholder}
              required={field.required}
              disabled={submitting}
              onChange={(newValue) =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === 'email' && state.extraConfirmationField
                      ? { ...state, extraConfirmationField: { ...state.extraConfirmationField, value: newValue } }
                      : state
                )
              }
              onBlur={() =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === 'email' && state.extraConfirmationField
                      ? { ...state, extraConfirmationField: { ...state.extraConfirmationField, showErrors: true } }
                      : state
                )
              }
              errorMessage={
                field.extraConfirmationField.showErrors ? emailExtraConfirmationErrorMessage(field) : undefined
              }
            />
          )}
        </div>
      )
    }
    case 'phone': {
      return (
        <FormControls.FormItemInGroup
          {...field}
          controlId={field.id}
          disabled={submitting}
          onChange={(newValue) =>
            field.setQuestion((state): Question => (state.kind === 'phone' ? { ...state, value: newValue } : state))
          }
          onBlur={setShowErrors}
          errorMessage={
            field.showErrors ? stringValueRequiredErrorMessage(field) || phoneInvalidErrorMessage(field) : undefined
          }
        />
      )
    }
    case 'fullName': {
      const fullNameErrorId = `${field.id}-full-name-error`

      let rowClassname:string = '50-50'
      if(
        (field.subFields.middle && !field.subFields.prefix && !field.subFields.suffix) ||
        (!field.subFields.middle && field.subFields.prefix && !field.subFields.suffix)
       ) {
        rowClassname = `30-30-40`
      }
      if(field.subFields.middle && field.subFields.prefix && !field.subFields.suffix) {
        rowClassname = `20-30-20-30`
      }
      if(field.subFields.middle && !field.subFields.prefix && field.subFields.suffix) {
        rowClassname = `30-20-30-20`
      }
      if(field.subFields.middle && field.subFields.prefix && field.subFields.suffix) {
        rowClassname = `15-25-15-30-15`
      }

      return (
        <Form.Group
          ref={fullNameRef}
          as="fieldset"
          className='fs__group--fullname'
          onBlur={() => {
            if (fullNameRef.current && !fullNameRef.current.matches(':focus-within')) setShowErrors()
          }}
        >
          <Form.Label as="legend" className={getFSClassName('formLabelClassName')}>
            {field.label}
            <FormControls.RequiredLabel required={field.required} />
          </Form.Label>

          {/* Add .is-invalid class to fs__input-wrapper div, because `Form.Control.Feedback` is shown through CSS sibling selector (.is-invalid ~ .invalid-feedback) */}
          <div className={`fs__input-wrapper fs__row row--${rowClassname}${fullNameRequiredErrorMessage(field) ? ' is-invalid' : ''}`}>
            {/* Prefix (select or input) */}
            {field.subFields.prefix &&
              (field.subFields.prefix.options ? (
                <FormControls.FormSelectInGroup
                  controlId={`${field.id}-${slugify(field.subFields.prefix.label)}`}
                  label={field.subFields.prefix.label}
                  value={field.subFields.prefix.value}
                  required={false}
                  disabled={submitting}
                  options={field.subFields.prefix.options.map((option) => ({ label: option, value: option }))}
                  onChange={(newValue) =>
                    field.setQuestion(
                      (state): Question =>
                        state.kind === 'fullName' && state.subFields.prefix
                          ? {
                              ...state,
                              subFields: { ...state.subFields, prefix: { ...state.subFields.prefix, value: newValue } },
                            }
                          : state
                    )
                  }
                />
              ) : (
                <FormControls.FormItemInGroup
                  controlId={`${field.id}-${slugify(field.subFields.prefix.label)}`}
                  label={field.subFields.prefix.label}
                  value={field.subFields.prefix.value}
                  required={false}
                  disabled={submitting}
                  errorId={fullNameErrorId}
                  onChange={(newValue) =>
                    field.setQuestion(
                      (state): Question =>
                        state.kind === 'fullName' && state.subFields.prefix
                          ? {
                              ...state,
                              subFields: { ...state.subFields, prefix: { ...state.subFields.prefix, value: newValue } },
                            }
                          : state
                    )
                  }
                />
              ))}

            {/* First name */}
            <FormControls.FormItemInGroup
              required={field.required}
              controlId={field.id}
              label={field.subFields.first.label}
              value={field.subFields.first.value}
              disabled={submitting}
              invalid={field.subFields.first.showErrors && firstRequiredInvalid(field)}
              errorId={fullNameErrorId}
              onChange={(newValue) =>
                field.setQuestion(
                  (state): Question =>
                    state.kind == 'fullName'
                      ? {
                          ...state,
                          subFields: { ...state.subFields, first: { ...state.subFields.first, value: newValue } },
                        }
                      : state
                )
              }
              onBlur={() =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === 'fullName'
                      ? {
                          ...state,
                          subFields: { ...state.subFields, first: { ...state.subFields.first, showErrors: true } },
                        }
                      : state
                )
              }
            />
            {/* Middle name */}
            {field.subFields.middle && (
              <FormControls.FormItemInGroup
                required={false}
                controlId={field.id}
                label={field.subFields.middle.label}
                value={field.subFields.middle.value}
                disabled={submitting}
                errorId={fullNameErrorId}
                onChange={(newValue) =>
                  field.setQuestion(
                    (state): Question =>
                      state.kind == 'fullName' && state.subFields.middle
                        ? {
                            ...state,
                            subFields: { ...state.subFields, middle: { ...state.subFields.middle, value: newValue } },
                          }
                        : state
                  )
                }
              />
            )}

            {/* Last name */}
            <FormControls.FormItemInGroup
              required={field.required}
              controlId={field.id}
              label={field.subFields.last.label}
              value={field.subFields.last.value}
              disabled={submitting}
              invalid={field.subFields.last.showErrors && lastRequiredInvalid(field)}
              errorId={fullNameErrorId}
              onChange={(newValue) =>
                field.setQuestion(
                  (state): Question =>
                    state.kind == 'fullName'
                      ? {
                          ...state,
                          subFields: { ...state.subFields, last: { ...state.subFields.last, value: newValue } },
                        }
                      : state
                )
              }
              onBlur={() =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === 'fullName'
                      ? {
                          ...state,
                          subFields: { ...state.subFields, last: { ...state.subFields.last, showErrors: true } },
                        }
                      : state
                )
              }
            />

            {/* Suffix name */}
            {field.subFields.suffix && (
              <FormControls.FormItemInGroup
                required={false}
                controlId={field.id}
                label={field.subFields.suffix.label}
                value={field.subFields.suffix.value}
                disabled={submitting}
                errorId={fullNameErrorId}
                onChange={(newValue) =>
                  field.setQuestion(
                    (state): Question =>
                      state.kind == 'fullName' && state.subFields.suffix
                        ? {
                            ...state,
                            subFields: { ...state.subFields, suffix: { ...state.subFields.suffix, value: newValue } },
                          }
                        : state
                  )
                }
              />
            )}
          </div>

          <FormControls.FormErrorText
            errorMessageId={fullNameErrorId}
            errorMessage={field.showErrors ? fullNameRequiredErrorMessage(field) : undefined}
          />
        </Form.Group>
      )
    }
    case 'radio': {
      const radioOptions = field.extraOption ? [...field.options, field.extraOption.label] : field.options
      return (
        <>
          <FormControls.FormRadioInGroup
            {...field}
            legend={field.label}
            name={field.id}
            items={radioOptions.map((option, i) => ({
              label: option,
              controlId: `rb-${field.id}-${slugify(option)}`,
              checked: field.value == option,
              onChange: (newValue) =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === 'radio' ? { ...state, value: newValue ? option : state.value } : state
                ),
            }))}
            disabled={submitting}
            onBlur={setShowErrors}
            errorMessage={field.showErrors ? stringValueRequiredErrorMessage(field) : undefined}
          />

          {field.extraOption && field.value == field.extraOption.label && (
            <FormControls.FormItemInGroup
              {...field}
              controlId={`rb-other-${field.id}`}
              label={field.extraOption.label}
              value={field.extraOption.value}
              onChange={(newValue) =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === 'radio' && state.extraOption
                      ? { ...state, extraOption: { ...state.extraOption, value: newValue } }
                      : state
                )
              }
              onBlur={() =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === field.kind && state.extraOption
                      ? { ...state, extraOption: { ...state.extraOption, showErrors: true } }
                      : state
                )
              }
              disabled={submitting}
              errorMessage={field.extraOption.showErrors ? radioExtraOptionErrorMessage(field) : undefined}
            />
          )}
        </>
      )
    }
    case 'checkbox': {
      return (
        <>
          <FormControls.FormCheckInGroup
            {...field}
            legend={field.label}
            name={field.id}
            items={(field.extraOption ? [...field.options, field.extraOption.label] : field.options).map((option) => ({
              label: option,
              controlId: `cb-${field.id}-${slugify(option)}`,
              checked: field.value.includes(option),
              onChange: (newValue) =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === 'checkbox'
                      ? {
                          ...state,
                          value: newValue ? state.value.concat(option) : state.value.filter((val) => val !== option),
                        }
                      : state
                ),
            }))}
            onBlur={setShowErrors}
            disabled={submitting}
            errorMessage={field.showErrors ? checkBoxErrorMessage(field) : undefined}
          />

          {field.extraOption && field.value.includes(field.extraOption.label) && (
            <FormControls.FormItemInGroup
              {...field}
              controlId={`cb-other-${field.id}`}
              label={field.extraOption.label}
              value={field.extraOption.value}
              onChange={(newValue) =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === 'checkbox' && state.extraOption
                      ? { ...state, extraOption: { ...state.extraOption, value: newValue } }
                      : state
                )
              }
              onBlur={() =>
                field.setQuestion(
                  (state): Question =>
                    state.kind === field.kind && state.extraOption
                      ? { ...state, extraOption: { ...state.extraOption, showErrors: true } }
                      : state
                )
              }
              disabled={submitting}
              errorMessage={field.extraOption.showErrors ? checkboxExtraOptionErrorMessage(field) : undefined}
            />
          )}
        </>
      )
    }
    case 'submitButton': {
      return (
        <button type="submit" className="button button--primary" disabled={submitting}>
          <span className="button__label">
            <FormControls.SubmittingSpinnerOrLabel label={field.label} showLoader={submitting} />
          </span>
        </button>
      )
    }
  }
}
